import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';
import type { components as visionComponents } from '@/api/types.vision.gen';

// Хуки раздела «Владелец платформы» (M5.4). Все эндпоинты — под ролью
// platform_admin (backend отдаёт 403 остальным). Паттерн — как в
// `@/settings/hooks.ts`: TanStack Query + типы из types.gen.ts.

export type SmsOverview = components['schemas']['SmsOverview'];
export type BalanceStatus = components['schemas']['BalanceStatus'];
export type SmsKpi = components['schemas']['SmsKpi'];
export type TenantSpend = components['schemas']['TenantSpend'];
export type SmsJournalPage = components['schemas']['SmsJournalPage'];
export type JournalEntry = components['schemas']['JournalEntry'];
export type PlatformSettings = components['schemas']['PlatformSettings'];
export type PlatformSettingsUpdate = components['schemas']['PlatformSettingsUpdate'];
export type PlatformAppSettings = components['schemas']['PlatformAppSettings'];
export type PlatformAppSettingsUpdate = components['schemas']['PlatformAppSettingsUpdate'];
export type PlatformAppKey = PlatformAppSettings['app'];
export type PlatformAdminList = components['schemas']['PlatformAdminList'];
export type PlatformAdminRead = components['schemas']['PlatformAdminRead'];
export type PlatformAdminCreate = components['schemas']['PlatformAdminCreate'];
export type PlatformAdminUpdate = components['schemas']['PlatformAdminUpdate'];
export type OpticRead = components['schemas']['OpticRead'];
export type OpticList = components['schemas']['OpticList'];
export type OpticBlockRequest = components['schemas']['OpticBlockRequest'];

// Уровень баланса для акцентов дашборда. Контракт даёт только булев `blocked`
// (баланс ниже порога → SMS остановлены). Промежуточный `warn` («близко к
// порогу») — UI-эвристика прототипа: баланс ещё выше порога, но менее чем в
// 1.6× от него. Это чисто визуальный «жёлтый» акцент, backend его не считает.
export type BalanceLevel = 'ok' | 'warn' | 'blocked';

export function balanceLevel(b: BalanceStatus): BalanceLevel {
  if (b.blocked) return 'blocked';
  if (b.control_enabled && b.balance_kzt !== null && b.balance_kzt < b.threshold_kzt * 1.6) {
    return 'warn';
  }
  return 'ok';
}

const OVERVIEW_KEY = ['platform', 'sms-overview'] as const;
const SETTINGS_KEY = ['platform', 'settings'] as const;
const APPS_KEY = ['platform', 'apps'] as const;
const ADMINS_KEY = ['platform', 'admins'] as const;
const OPTICS_KEY = ['platform', 'optics'] as const;

// SMS-центр — баланс SMSC + KPI + расход по оптикам. staleTime 30 сек:
// баланс на бэке синхронизируется периодически (~30 мин), но свежую
// блокировку / расход хотим видеть быстро; refetchOnWindowFocus помогает.
export function useSmsOverviewQuery() {
  return useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api.get<SmsOverview>('/api/v1/platform/sms-overview'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

// Кнопка «Обновить баланс» — синхронный опрос SMSC on-demand (0.5.3). В отличие
// от простого refetch overview (читает Redis-кэш, часто пустой), реально дёргает
// SMSC и пишет свежий баланс, затем инвалидирует overview.
export function useRefreshBalanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['platform', 'sms-balance', 'refresh'],
    mutationFn: () => api.post<BalanceStatus>('/api/v1/platform/sms-balance/refresh'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
  });
}

export interface SmsJournalParams {
  page: number;
  pageSize: number;
  channel?: string;
  status?: string;
}

function buildJournalUrl(params: SmsJournalParams): string {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.pageSize),
  });
  if (params.channel) sp.set('channel', params.channel);
  if (params.status) sp.set('status', params.status);
  return `/api/v1/platform/sms-journal?${sp.toString()}`;
}

export type OtpJournalEntry = components['schemas']['OtpJournalEntry'];
export type OtpJournalPage = components['schemas']['OtpJournalPage'];

export interface OtpJournalParams {
  page: number;
  pageSize: number;
  flow?: string;
  status?: string;
  phone?: string;
}

// Журнал OTP-SMS (№20): коды входа/верификации/контактов — вне журнала
// `message`, своя таблица `otp_sms_log`. GET /platform/otp-journal.
export function useOtpJournalQuery(params: OtpJournalParams, options?: { enabled?: boolean }) {
  const sp = new URLSearchParams({ page: String(params.page), page_size: String(params.pageSize) });
  if (params.flow) sp.set('flow', params.flow);
  if (params.status) sp.set('status', params.status);
  if (params.phone) sp.set('phone', params.phone);
  return useQuery({
    queryKey: ['platform', 'otp-journal', params],
    queryFn: () => api.get<OtpJournalPage>(`/api/v1/platform/otp-journal?${sp.toString()}`),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}

export function useSmsJournalQuery(params: SmsJournalParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['platform', 'sms-journal', params],
    queryFn: () => api.get<SmsJournalPage>(buildJournalUrl(params)),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}

export function usePlatformSettingsQuery() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.get<PlatformSettings>('/api/v1/platform/settings'),
    staleTime: 30_000,
  });
}

// Профили приложений платформы (доводка V1 №15/№19): бренд, From, база
// ссылок в письмах, тумблер «Объявлений». GET /platform/apps, PATCH /platform/apps/{app}.
export function usePlatformAppsQuery() {
  return useQuery({
    queryKey: APPS_KEY,
    queryFn: () =>
      api.get<{ items: PlatformAppSettings[] }>('/api/v1/platform/apps').then((r) => r.items),
  });
}

export function useUpdatePlatformAppMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...APPS_KEY, 'update'],
    mutationFn: ({ app, ...input }: PlatformAppSettingsUpdate & { app: PlatformAppKey }) =>
      api.patch<PlatformAppSettings>(`/api/v1/platform/apps/${app}`, input),
    onSuccess: (updated) => {
      qc.setQueryData<PlatformAppSettings[]>(APPS_KEY, (prev) =>
        prev ? prev.map((a) => (a.app === updated.app ? updated : a)) : prev,
      );
      // Тумблер «Объявлений» читает и кабинет оптики (публичные фича-флаги).
      void qc.invalidateQueries({ queryKey: ['apps', 'features'] });
    },
  });
}

export function useUpdatePlatformSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['platform', 'settings', 'update'],
    mutationFn: (input: PlatformSettingsUpdate) =>
      api.patch<PlatformSettings>('/api/v1/platform/settings', input),
    onSuccess: (updated) => {
      qc.setQueryData(SETTINGS_KEY, updated);
      // Порог/флаг контроля влияют на баланс-статус на дашборде.
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
    },
  });
}

// Справочник оптик платформы (M5.7) — управление и блокировка. Отдельный
// эндпоинт от sms-overview: здесь контакты и статус активности, без расхода.
export function useOpticsQuery() {
  return useQuery({
    queryKey: OPTICS_KEY,
    queryFn: () => api.get<OpticList>('/api/v1/platform/optics'),
    staleTime: 30_000,
  });
}

// Мягкая блокировка оптики: причина обязательна (backend валидирует непустую).
export function useBlockOpticMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['platform', 'optics', 'block'],
    mutationFn: ({ tenantId, reason }: { tenantId: string; reason: string }) =>
      api.post<OpticRead>(`/api/v1/platform/optics/${tenantId}/block`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPTICS_KEY });
    },
  });
}

export function useUnblockOpticMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['platform', 'optics', 'unblock'],
    mutationFn: ({ tenantId }: { tenantId: string }) =>
      api.post<OpticRead>(`/api/v1/platform/optics/${tenantId}/unblock`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPTICS_KEY });
    },
  });
}

export function usePlatformAdminsQuery() {
  return useQuery({
    queryKey: ADMINS_KEY,
    queryFn: () => api.get<PlatformAdminList>('/api/v1/platform/admins'),
  });
}

export function useCreatePlatformAdminMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['platform', 'admins', 'create'],
    mutationFn: (input: PlatformAdminCreate) =>
      api.post<PlatformAdminRead>('/api/v1/platform/admins', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMINS_KEY });
    },
  });
}

export function useUpdatePlatformAdminMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['platform', 'admins', 'update'],
    mutationFn: ({ id, status }: { id: string; status: PlatformAdminUpdate['status'] }) =>
      api.patch<PlatformAdminRead>(`/api/v1/platform/admins/${id}`, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMINS_KEY });
    },
  });
}

// ─── Модерация объявлений Vision (V1.3-d) ────────────────────────────────────
// Раздел /platform/moderation ходит в продуктовый неймспейс
// /api/v1/vision/admin/* (VD10: гейт платформенный, данные — продукта Vision).
// Типы — из ВТОРОГО генерата `types.vision.gen.ts` (контракт openapi-vision.yaml);
// импортировать их из types.gen.ts нельзя — контракты у продуктов раздельные.
export type AdminListingReport = visionComponents['schemas']['AdminListingReport'];
export type AdminReportPage = visionComponents['schemas']['AdminReportPage'];
export type VisionListing = visionComponents['schemas']['Listing'];
export type VisionListingStatus = visionComponents['schemas']['ListingStatus'];
export type ListingReportResolution = visionComponents['schemas']['ListingReportResolution'];
export type ModerationReportsState = 'open' | 'resolved';

const MODERATION_KEY = ['platform', 'moderation'] as const;

export interface ModerationReportsParams {
  /** `open` — очередь (FIFO), `resolved` — журнал разобранных (свежие сверху). */
  state: ModerationReportsState;
  page: number;
  pageSize: number;
}

// Очередь НЕразрешённых жалоб (FIFO, старые сверху) либо журнал разобранных
// (свежие решения сверху; исход + кто разобрал — доводка №18). Страничная
// пагинация D6.
export function useModerationReportsQuery(
  params: ModerationReportsParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...MODERATION_KEY, 'reports', params],
    queryFn: () =>
      api.get<AdminReportPage>(
        `/api/v1/vision/admin/reports?state=${params.state}&page=${params.page}&page_size=${params.pageSize}`,
      ),
    placeholderData: (prev) => prev,
    // Вкладка «Реклама» (V1.4) живёт на той же странице — жалобы там не нужны.
    enabled: options?.enabled ?? true,
  });
}

// Снять объявление: status=removed + бэкенд авторезолвит все его открытые
// жалобы — поэтому после успеха инвалидируем всю очередь (уйти может
// несколько строк, не только кликнутая).
export function useRemoveListingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...MODERATION_KEY, 'remove'],
    mutationFn: ({ listingId }: { listingId: string }) =>
      api.post<VisionListing>(`/api/v1/vision/admin/listings/${listingId}/remove`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MODERATION_KEY });
    },
  });
}

// Восстановить снятое модерацией (только removed → active). `archived`
// (снял автор) бэкенд отбивает 409 `listing_not_removed` — обрабатывает UI.
export function useRestoreListingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...MODERATION_KEY, 'restore'],
    mutationFn: ({ listingId }: { listingId: string }) =>
      api.post<VisionListing>(`/api/v1/vision/admin/listings/${listingId}/restore`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MODERATION_KEY });
    },
  });
}

// Закрыть жалобу без снятия объявления («не подтвердилась»).
export function useResolveReportMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...MODERATION_KEY, 'resolve'],
    mutationFn: ({ reportId }: { reportId: string }) =>
      api.post<AdminListingReport>(`/api/v1/vision/admin/reports/${reportId}/resolve`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MODERATION_KEY });
    },
  });
}

// ─── Платные услуги (V1.4) ────────────────────────────────────────────────────
// Раздел «Платные услуги» настроек платформы: тарифы по приложениям
// (`GET /platform/pricing`), правка одной строки (`PATCH /platform/pricing/{app}/{code}`).
// Сейчас одна живая услуга — vision/ad_week (реклама, ₸ за неделю показа);
// `payment_note` уходит заявителю в письме после одобрения (ручная оплата пилота).
export type PriceItem = components['schemas']['PriceItem'];
export type PricingList = components['schemas']['PricingList'];
export type PriceItemUpdate = components['schemas']['PriceItemUpdate'];
export type PriceApp = PriceItem['app'];

const PRICING_KEY = ['platform', 'pricing'] as const;

export function usePricingQuery() {
  return useQuery({
    queryKey: PRICING_KEY,
    queryFn: () => api.get<PricingList>('/api/v1/platform/pricing').then((r) => r.items),
    staleTime: 30_000,
  });
}

export function useUpdatePriceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...PRICING_KEY, 'update'],
    mutationFn: ({ app, code, ...input }: PriceItemUpdate & { app: PriceApp; code: string }) =>
      api.patch<PriceItem>(`/api/v1/platform/pricing/${app}/${code}`, input),
    onSuccess: (updated) => {
      qc.setQueryData<PriceItem[]>(PRICING_KEY, (prev) =>
        prev
          ? prev.map((p) => (p.app === updated.app && p.code === updated.code ? updated : p))
          : prev,
      );
      // Публичный тариф читает Vision (форма заявки) — в Light его кэша нет,
      // но ключ держим общим на случай общего QueryClient в будущем.
      void qc.invalidateQueries({ queryKey: ['apps', 'vision', 'pricing'] });
    },
  });
}
